import { audio, type Cue } from "./engine.ts";

/**
 * Baked cues. Synthesizing a gunshot costs a dozen nodes with per-sample filter sweeps; a
 * continuously firing rifle alone takes ~9% of a core. Frequent cues (weapons, feet, hit ticks,
 * impacts, explosions) are therefore rendered offline into a small pool of random variations the
 * first time they're needed, and played back as a single buffer with a little pitch jitter.
 * Until a pool is ready the cue synthesizes live, so nothing is ever silent.
 */

interface Pool {
  buffers?: AudioBuffer[];
  pending?: Promise<void>;
}

const pools = new Map<string, Pool>();

/** A baked cue can be asked to bake ahead of time. */
export type BakedCue = Cue & { warm(): Promise<void> | undefined };

export interface BakeOpts {
  /** Distinct random renders kept per cue. */
  variants?: number;
  /** ± playback-rate jitter per play (pitch and length together). */
  jitter?: number;
  /** Fixed playback-rate multiplier (pitch steps for combos, bounces…). */
  rate?: number;
}

async function renderPool(cue: Cue, variants: number): Promise<AudioBuffer[]> {
  const sr = audio.ctx?.sampleRate ?? 48000;
  const probe = new OfflineAudioContext(1, 128, sr);
  const len = Math.min(6, cue({ ctx: probe, dest: probe.destination, t: 0 }) + 0.05);
  const slot = Math.ceil(len * sr);
  const ctx = new OfflineAudioContext(1, slot * variants, sr);
  for (let i = 0; i < variants; i++) cue({ ctx, dest: ctx.destination, t: (i * slot) / sr + 0.002 });
  await Promise.resolve();
  const all = await ctx.startRendering();
  const src = all.getChannelData(0);
  return Array.from({ length: variants }, (_, i) => {
    // Trim trailing silence so short cues don't hold a voice for their whole slot.
    const seg = src.subarray(i * slot, (i + 1) * slot);
    let end = seg.length;
    while (end > 1 && Math.abs(seg[end - 1]!) < 1e-4) end--;
    const b = new AudioBuffer({ length: Math.max(1, end + 64), numberOfChannels: 1, sampleRate: sr });
    b.copyToChannel(seg.subarray(0, Math.max(1, end + 64)), 0);
    return b;
  });
}

function ensure(key: string, make: () => Cue, variants: number): Pool {
  let pool = pools.get(key);
  if (!pool) {
    pool = {};
    pools.set(key, pool);
    const p = pool;
    p.pending = renderPool(make(), variants).then(
      (b) => {
        p.buffers = b;
        p.pending = undefined;
      },
      (e) => console.warn("audio: bake failed", key, e),
    );
  }
  return pool;
}

/** A cue that plays from a baked pool (keyed by `key`) once one exists. */
export function baked(key: string, make: () => Cue, o: BakeOpts = {}): BakedCue {
  const variants = o.variants ?? 4;
  const cue: Cue = (out) => {
    const pool = ensure(key, make, variants);
    const bufs = pool.buffers;
    if (!bufs) return make()(out);
    const buf = bufs[Math.floor(Math.random() * bufs.length)]!;
    const rate = (o.rate ?? 1) * (1 + (Math.random() * 2 - 1) * (o.jitter ?? 0.02));
    const src = out.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    src.connect(out.dest);
    src.start(out.t);
    return buf.duration / rate;
  };
  return Object.assign(cue, { warm: () => ensure(key, make, variants).pending });
}

/** Bake cues ahead of need (at match load), one at a time so the audio thread stays free. */
export async function prewarm(list: Cue[]) {
  for (const c of list) await (c as Partial<BakedCue>).warm?.();
}

export const bakedCount = () => [...pools.values()].filter((p) => p.buffers).length;
